import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Scale, CheckCircle, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";

interface Reading {
  type: "tare" | "gross";
  weight: number;
  timestamp: Date;
}

interface SessionControlPanelProps {
  visitId?: number;
  laneId: number;
  onSessionCreated: (sessionId: number) => void;
}

export default function SessionControlPanel({ visitId, laneId, onSessionCreated }: SessionControlPanelProps) {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [currentMode, setCurrentMode] = useState<"tare" | "gross" | "idle">("idle");
  const [readings, setReadings] = useState<Reading[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createSessionMutation = useMutation({
    mutationFn: (data: { visitId: number; laneId: number }) => 
      api.createSession(data),
    onSuccess: (session) => {
      setSessionId(session.id);
      onSessionCreated(session.id);
      toast({
        title: "Weighing Session Started",
        description: `Session #${session.id} created for Lane ${laneId}`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create session",
        variant: "destructive",
      });
    }
  });

  const addReadingMutation = useMutation({
    mutationFn: (data: { sessionId: number; type: string; value: number }) =>
      api.addReading(data.sessionId, { type: data.type, value: data.value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
    }
  });

  const closeSessionMutation = useMutation({
    mutationFn: (sessionId: number) => api.closeSession(sessionId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/metrics"] });
      toast({
        title: "Session Completed",
        description: `Ticket ${result.ticket.ticketNo} generated`,
      });
      // Reset state
      setSessionId(null);
      setCurrentMode("idle");
      setReadings([]);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to close session",
        variant: "destructive",
      });
    }
  });

  const handleStartSession = () => {
    if (!visitId) {
      toast({
        title: "No Visit Selected",
        description: "Please select a visit to start weighing session",
        variant: "destructive",
      });
      return;
    }
    createSessionMutation.mutate({ visitId, laneId });
  };

  const handleSetMode = (mode: "tare" | "gross") => {
    setCurrentMode(mode);
  };

  const handleReading = (type: "tare" | "gross", weight: number, stable: boolean) => {
    if (!sessionId || !stable) return;

    const reading: Reading = {
      type,
      weight,
      timestamp: new Date()
    };

    setReadings(prev => [...prev, reading]);
    
    // Submit to API
    addReadingMutation.mutate({
      sessionId,
      type,
      value: weight
    });

    // Automatically progress to next mode
    if (type === "tare") {
      setCurrentMode("gross");
      toast({
        title: "Tare Weight Captured",
        description: `${weight.toLocaleString()} kg - Now capture gross weight`,
      });
    } else {
      setCurrentMode("idle");
      toast({
        title: "Gross Weight Captured", 
        description: `${weight.toLocaleString()} kg - Ready to complete session`,
      });
    }
  };

  const handleCompleteSession = () => {
    if (!sessionId) return;
    
    const tareReading = readings.find(r => r.type === "tare");
    const grossReading = readings.find(r => r.type === "gross");
    
    if (!tareReading || !grossReading) {
      toast({
        title: "Incomplete Readings",
        description: "Both tare and gross weights are required",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    closeSessionMutation.mutate(sessionId);
  };

  const netWeight = () => {
    const tareReading = readings.find(r => r.type === "tare");
    const grossReading = readings.find(r => r.type === "gross");
    if (tareReading && grossReading) {
      return Math.max(grossReading.weight - tareReading.weight, 0);
    }
    return 0;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Scale className="w-5 h-5 mr-2" />
            Session Control
          </div>
          {sessionId && (
            <Badge className="bg-green-100 text-green-800">
              Session #{sessionId}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Session Status */}
          {!sessionId ? (
            <div className="text-center py-4">
              <Button
                onClick={handleStartSession}
                disabled={!visitId || createSessionMutation.isPending}
                className="bg-primary text-primary-foreground"
                data-testid="button-start-session"
              >
                {createSessionMutation.isPending ? "Starting..." : "Start Weighing Session"}
              </Button>
              {!visitId && (
                <p className="text-sm text-gray-500 mt-2">
                  Select a visit first to begin weighing
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Mode Selection */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => handleSetMode("tare")}
                  variant={currentMode === "tare" ? "default" : "outline"}
                  disabled={readings.some(r => r.type === "tare")}
                  data-testid="button-mode-tare"
                >
                  Tare Weight
                  {readings.some(r => r.type === "tare") && (
                    <CheckCircle className="w-4 h-4 ml-2 text-green-600" />
                  )}
                </Button>
                
                <Button
                  onClick={() => handleSetMode("gross")}
                  variant={currentMode === "gross" ? "default" : "outline"}
                  disabled={!readings.some(r => r.type === "tare") || readings.some(r => r.type === "gross")}
                  data-testid="button-mode-gross"
                >
                  Gross Weight
                  {readings.some(r => r.type === "gross") && (
                    <CheckCircle className="w-4 h-4 ml-2 text-green-600" />
                  )}
                </Button>
              </div>

              {/* Readings Display */}
              {readings.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Captured Readings</h4>
                  <div className="space-y-2">
                    {readings.map((reading, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="capitalize font-medium">{reading.type}:</span>
                        <span>{reading.weight.toLocaleString()} kg</span>
                      </div>
                    ))}
                    {readings.length === 2 && (
                      <div className="flex justify-between text-sm font-bold border-t pt-2">
                        <span>Net Weight:</span>
                        <span className="text-green-600">{netWeight().toLocaleString()} kg</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Complete Session */}
              {readings.length === 2 && (
                <Button
                  onClick={handleCompleteSession}
                  disabled={isProcessing || closeSessionMutation.isPending}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  data-testid="button-complete-session"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {isProcessing ? "Generating Ticket..." : "Complete Session & Generate Ticket"}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}